import { AfterViewChecked, AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AppNotificationEventPreferencesComponent, IAppNotificationEventPreferencesComponentResult } from './app-notification-event-preferences/app-notification-event-preferences.component';
import { AppNotificationService } from './app-notification.service';
import { AppNotificationEvent } from './models/app-notification-event';
import { AppNotificationGroup } from './models/app-notification-group';
import { AppNotificationUserEvent, IAppNotificationUserEvent } from './models/app-notification-user-event.model';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-notification-component',
    templateUrl: './app-notification.component.html',
    styleUrls: ['./app-notification.component.scss'],
    providers: [AppNotificationService],
    standalone: false
})
export class AppNotificationComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {

    isAppSelected = false;
    isReceivingForSelectedApp = false;
    readonly groupCheckBoxIdPrefix = 'group-checkbox-';
    SELECTION_LEVEL: { [prop: string]: { 'isActive': boolean, data: IAppNotificationUserEvent[] } } = {
        'APP': {
            'isActive': false, data: []
        },
        'GROUP': {
            'isActive': false, data: []
        },
        'EVENT': {
            'isActive': false, data: []
        }
    };
    readonly checkboxPosition = 'start';
    pageBusy = false;
    appNotificationForm: UntypedFormGroup;
    notificationGroups: AppNotificationGroup[] = []; // for notification group
    notificationEvents: AppNotificationEvent[] = []; // for event buttons

    // @ViewChildren(AppNotificationEventButtonComponent)
    // private eventButtons: QueryList<AppNotificationEventButtonComponent>;

    @ViewChildren(MatCheckbox)
    private checkBoxes: QueryList<MatCheckbox>;

    get userApps() { return this._userProfile.apps; }
    // get isReceivingForApp(): boolean {
    //     let isReceivingForApp = false;
    //     for (const e of this._userData) {
    //         if (RegExp(e.appId, 'i').test(this._selectedApp)) {
    //             isReceivingForApp = true; break;
    //         }
    //     }
    //     return isReceivingForApp;
    // }

    private readonly selectionLevels = { 'APP': 1, 'GROUP': 2, 'EVENT': 3 };
    private readonly NOTIFICATION_DIALOG_WIDTH = '400px';
    private _userProfile: { [key: string]: any }; // current user's profile
    private _groups: AppNotificationGroup[] = []; // currently displayed notification groups
    private _allNotificationEvents: AppNotificationEvent[] = []; // event buttons
    private _selectedApp: string; // currently selected app
    private _userData: AppNotificationUserEvent[] = []; // user data fetched on init
    private _userDataOriginal: AppNotificationUserEvent[] = [];

    private userData$: Subscription;
    private allNotificationEvents$: Subscription;
    private notificationPreferencesUpdatedSubscription$: Subscription;
    private notificationGroups$: Subscription;

    constructor(private appNotificationService: AppNotificationService,
        private dialog: MatDialog,
        private alert: MatSnackBar) {
    }

    ngOnInit() {
        this._userProfile = JSON.parse(this.appNotificationService.getProfile());
        this.pageBusy = true;
        this.appNotificationService.getOrganisationDefaultNotificationLevel().subscribe(res => {
            if (res && res.hasOwnProperty('defaultNotificationLevel') &&
                typeof res.defaultNotificationLevel === 'string' &&
                (new RegExp('^(EVENT|APP|GROUP)$', 'i').test(res.defaultNotificationLevel.trim()))) {
                const level: string = res.defaultNotificationLevel.trim().toUpperCase();
                this.SELECTION_LEVEL[level].isActive = true;
            }
        }, error => {
            this.SELECTION_LEVEL.EVENT.isActive = true;
        }, () => {
            this.pageBusy = false;
        }
        );
        this.initializeForm();
        this.createSubscriptions();
        this.appNotificationService.getUserEventsData(); // load user's data on init
    }

    ngAfterViewInit() { }

    ngAfterViewChecked() {
        if (this.checkBoxes.length > 0) {
            this.checkBoxes.forEach((cb: MatCheckbox) => {
                (cb._elementRef.nativeElement.getElementsByClassName('mat-checkbox-layout')[0] as HTMLElement).style.width = '100%';
                const label: HTMLElement = cb._elementRef.nativeElement.getElementsByClassName('mat-checkbox-label')[0] as HTMLElement;
                if (!label.style.textOverflow) {
                    label.style.whiteSpace = 'noWrap';
                    label.style.overflow = 'hidden';
                    label.style.textOverflow = 'ellipsis';
                }
            });
        }
    }

    private createSubscriptions(): void {
        const self = this;
        self.userData$ = self.appNotificationService.watchUserData()
            .subscribe(function (data: AppNotificationUserEvent[]) {
                self.pageBusy = false;
                self._userDataOriginal.splice(0);
                self._userDataOriginal = data.map((e: AppNotificationUserEvent) => {
                    return new AppNotificationUserEvent(JSON.parse(JSON.stringify(e.getValue())));
                });
                self._userData = data;
            });
        // update groups
        self.notificationGroups$ = self.appNotificationService.watchNotificationGroups()
            .subscribe(function (data: AppNotificationGroup[]) {
                self.pageBusy = false;
                self._groups = data;
                // fetch events for all groups
                self.getNotificationEvents(self._groups.map((g: AppNotificationGroup) => g.id));
            });
        // update events
        self.allNotificationEvents$ = self.appNotificationService.watchNotificationEvents()
            .subscribe(function (data: AppNotificationEvent[]) {
                self.pageBusy = false;
                self._allNotificationEvents = data;
                if (self.SELECTION_LEVEL.APP.isActive) {
                    for (const g of self._groups) { self.groupChecked(g.id, true); }
                }
                self.updateNotificationGroups();
                self.updateNotificationEvents();
            });
    }
    private initializeForm(): void {
        const self = this;
        self.appNotificationForm = new UntypedFormGroup({
            app: new UntypedFormControl(null, [Validators.required])
        });
        self.appNotificationForm.get('app')
            .valueChanges.pipe(
            filter(function (data: string) {
                return (self._selectedApp !== data);
            }))
            .subscribe(function (value: string) {
                self.processAppSelection(value);
            });
    }
    private processAppSelection(selectedApp: string): void {
        this._selectedApp = selectedApp;
        this.isAppSelected = true;
        this.updateAppLevelSlider();
        /** fetch notification groups belonging to selected app */
        this.pageBusy = true;
        this.appNotificationService.getNotificationGroupsByApps([this._selectedApp]);
    }
    private openNotificationPreferencesDialog(dialogData: {
        'eventName': string,
        'data': any,
        'readonly'?: boolean,
        'saveButton'?: boolean
    }, cb: any): void {
        this.dialog
            .open(AppNotificationEventPreferencesComponent, {
                'width': this.NOTIFICATION_DIALOG_WIDTH,
                'data': dialogData
            })
            .afterClosed()
            .subscribe(cb);
    }
    /**
     * invoked when app-level slider is toggled
     */
    appLevelSliderToggle(isSubscribedToApp: boolean): void {
        if (!this.SELECTION_LEVEL.APP.isActive) {
            return;
        }
        this.SELECTION_LEVEL.APP.data.splice(0);
        const dialogData = {
            'userId': this._userProfile.email,
            'appId': this._selectedApp,
            'eventId': 0,
            'isEmailSubscribed': isSubscribedToApp ? true : false,
            'isInAppSubscribed': isSubscribedToApp ? true : false,
            'isPushSubscribed': isSubscribedToApp ? true : false,
            'isSmsSubscribed': isSubscribedToApp ? true : false
        };
        this.openNotificationPreferencesDialog({
            'eventName': '$APP-LEVEL-SELECTION$', 'data': dialogData, 'readonly': isSubscribedToApp ? false : true, 'saveButton': false
        }, (function (dialogResult: IAppNotificationEventPreferencesComponentResult) {
            // fetch groups in the selected app
            const groupIds = this._groups.filter((_g: AppNotificationGroup) => _g.appId === this._selectedApp);
            // find all events which belong to those groups
            this._allNotificationEvents.forEach((event: AppNotificationEvent) => {
                if (groupIds.indexOf(event.groupId) !== -1) {
                    dialogResult.data.eventId = event.id;
                    this.SELECTION_LEVEL.APP.data.push(dialogResult.data);
                }
            });
            this.showAlert('Your Changes Have Been Recorded. Please Click \'Submit\' To Save.');
        }).bind(this));
    }
    /**
     * fetches notification events based on their groups.
     * triggered when user selects an app from dropdown.
     * @param groupIds
     */
    private getNotificationEvents(groupIds: number[]): void {
        this.pageBusy = true;
        this.appNotificationService.getEventsByGroupIds(groupIds);
    }
    /**
     * update notification groups on ui
     */
    private updateNotificationGroups(): void {
        //  loop through all groups
        this._groups.forEach((group: AppNotificationGroup) => {
            //  count all events belonging to this group
            const totalEvents = (this._allNotificationEvents.filter((event: AppNotificationEvent) => {
                return event.groupId === group.id;
            })).length;

            const count = (this._userData.filter((userEvent: AppNotificationUserEvent) => {
                return userEvent.groupId === group.id && isFullySelected(userEvent);
            })).length;

            if (count === totalEvents) {
                group.isOptedByUser = true;
            } else if (count > 0 && count < totalEvents) {
                group.isPartiallyOpted = true;
            }
        });
        this.notificationGroups = [...this._groups];
        function isFullySelected(data: AppNotificationUserEvent) {
            return data.isEmailSubscribed && data.isInAppSubscribed && data.isPushSubscribed && data.isSmsSubscribed;
        }
    }
    /**
     * update event buttons on ui.
     */
    private updateNotificationEvents(): void {
        const userOptedEvents: number[] = [];
        for (const userEvent of this._userData) {
            if (userEvent.isEmailSubscribed || userEvent.isInAppSubscribed || userEvent.isPushSubscribed || userEvent.isSmsSubscribed) {
                userOptedEvents.push(userEvent.eventId);
            }
        }
        this.notificationEvents = this._allNotificationEvents.map((e: AppNotificationEvent) => {
            const event = { ...e };
            event.isOptedByUser = false;
            if (userOptedEvents.includes(event.id)) {
                event.isOptedByUser = true;
            }
            return event;
        });
        return;
    }
    getFullAppName(appId: string): string {
        const result = this.appNotificationService.appNames[appId.toString().trim()];
        if (result) {
            return result;
        } else {
            return appId;
        }
    }
    /**
     * when user checks / unchecks a notification group
     * @param checkEvent
     * @param selectedGroupId
     */
    groupChecked(selectedGroupId: number, checkEvent: MatCheckboxChange | boolean): void {
        let state: boolean = (typeof checkEvent === 'boolean') ? checkEvent : checkEvent.checked;
        const isCheckboxIndeterminate = (<MatCheckboxChange>checkEvent).source.indeterminate;
        //  allow selection if the selected checkbox is indeterminate
        if (isCheckboxIndeterminate) { state = true; }
        const handleGroupLevelSelection = foo.bind(this);
        const eventPreferences = {
            'isEmailSubscribed': state, 'isInAppSubscribed': state, 'isPushSubscribed': state, 'isSmsSubscribed': state
        };
        if (this.SELECTION_LEVEL.GROUP.isActive) {
            return handleGroupLevelSelection();
        }
        const createNewEntry = (eid: number, gid: number): IAppNotificationUserEvent => {
            const obj = {
                'userId': this._userProfile.email,
                'appId': this._selectedApp,
                'eventId': eid, 'groupId': gid
            };
            this.copyPreferences(obj, eventPreferences);
            return obj;
        };
        const optedEventsWithIndex: { [eventId: number]: number } = {};
        this._userData.forEach((e: AppNotificationUserEvent, i: number) => {
            if (e.userId === this._userProfile.email && e.groupId === selectedGroupId) {
                optedEventsWithIndex[e.eventId] = i;
            }
        });
        this._allNotificationEvents.forEach((event: AppNotificationEvent) => {
            if (event.groupId && event.groupId !== selectedGroupId) { return; }
            if (optedEventsWithIndex.hasOwnProperty(event.id)) {
                this.copyPreferences(this._userData[optedEventsWithIndex[event.id]], eventPreferences);
            } else {
                this._userData.push(new AppNotificationUserEvent(createNewEntry(event.id, event.groupId)));
            }
        });
        this.updateNotificationGroups();
        this.updateNotificationEvents();
        function foo() {
            this.openNotificationPreferencesDialog({
                eventName: '$MENU-LEVEL-SELECTION$',
                'data': {
                    'userId': this._userProfile.email,
                    'appId': this._selectedApp,
                    'eventId': 0,
                    'isEmailSubscribed': state,
                    'isInAppSubscribed': state,
                    'isPushSubscribed': state,
                    'isSmsSubscribed': state
                },
                'readonly': !state
            }, (function (result: IAppNotificationEventPreferencesComponentResult) {
                if (!result) {
                    return (() => {
                        const selectedBox = this.checkBoxes.find((box: MatCheckbox) =>
                            Number(box.id.split(this.groupCheckBoxIdPrefix)[1]) === selectedGroupId
                        );
                        if (isCheckboxIndeterminate) {
                            selectedBox.indeterminate = selectedBox.checked = true;
                            this.updateNotificationGroups();
                        } else {
                            selectedBox.checked = !state;
                        }
                    })();
                }
                // remove all changes made for this group, if any
                this.SELECTION_LEVEL.GROUP.data = this.SELECTION_LEVEL.GROUP.data.filter((d: AppNotificationUserEvent) =>
                    d.groupId !== selectedGroupId);
                // add event belonging to currently selected group
                this._allNotificationEvents.forEach((event: AppNotificationEvent) => {
                    if (event.groupId === selectedGroupId) {
                        result.data.eventId = event.id;
                        this.SELECTION_LEVEL.GROUP.data.push(result.data);
                    }
                });
                this.showAlert('Your Changes Have Been Recorded. Please Click \'Submit\' To Save.');
            }).bind(this));
            return;
        }
    }
    /**
     * when event button is clicked.
     * @param eventId
     */
    eventClicked(event: AppNotificationEvent): void {
        let selectedEvent = this._userData.find(
            (e: AppNotificationUserEvent) =>
                e.userId === this._userProfile.email && e.eventId === event.id
        );
        if (typeof selectedEvent === 'undefined') {
            selectedEvent = new AppNotificationUserEvent({
                'userId': this._userProfile.email,
                'appId': this._selectedApp,
                'eventId': event.id,
                'groupId': event.groupId
            });
        }
        this.openNotificationPreferencesDialog({
            'eventName': event.eventName,
            'data': selectedEvent.getValue()
        }, (function (result: IAppNotificationEventPreferencesComponentResult) {
            this.handleNotificationPreferencesUpdate(result);
        }).bind(this));
    }
    /**
     * when user closes the dialog.
     * @param dialogRef MatDialogRef<AppNotificationEventPreferencesComponent, any>
     */
    private handleNotificationPreferencesUpdate(result: IAppNotificationEventPreferencesComponentResult): void {
        if (!result || !result.isModified) {
            return;
        }
        (function (data: IAppNotificationUserEvent, cb1: () => void, cb2: () => void) {
            const preferences = {
                'isEmailSubscribed': data.isEmailSubscribed,
                'isInAppSubscribed': data.isInAppSubscribed,
                'isPushSubscribed': data.isPushSubscribed,
                'isSmsSubscribed': data.isSmsSubscribed,
                'isModified': true
            };
            const index = this._userData.findIndex((e: AppNotificationUserEvent) =>
                e.userId === data.userId && e.appId === data.appId && e.eventId === data.eventId
            );
            // update preferences in user data
            if (index > -1) {
                this.copyPreferences(this._userData[index], preferences);
            } else {
                this._userData.push(new AppNotificationUserEvent(data));
            }
            cb1();
            cb2();
            return;
        }).apply(this, [result.data, this.updateNotificationGroups.bind(this), this.updateNotificationEvents.bind(this)]);
    }
    /**
     * copies provided data into source object overwriting values if present.
     * @param source the source object
     * @param data
     */
    private copyPreferences(source: object, data: object): void {
        Object.keys(data).forEach(function (key: string) {
            source[key] = data[key];
        });
        return;
    }
    private showAlert(message: string, action = 'OK'): void {
        this.alert.open(message, action, { duration: 3000 });
    }
    /**
     * calculate the boolean value bound to the slider toggle which indicates
     * whether user is receiving notifications for the app he/she has selected
     */
    private updateAppLevelSlider(): void {
        if (this.isAppSelected && this.SELECTION_LEVEL.APP.isActive) {
            for (const e of this._userData) {
                if (RegExp(e.appId, 'i').test(this._selectedApp) &&
                    (e.isEmailSubscribed || e.isInAppSubscribed || e.isPushSubscribed || e.isSmsSubscribed)) {
                    this.isReceivingForSelectedApp = true;
                    break;
                }
            }
        }
    }
    submitForm(): void {
        if (this.appNotificationForm.invalid) {
            return;
        }
        let data: IAppNotificationUserEvent[] = this._userData
            .filter((e: AppNotificationUserEvent) => e.isModified && e.isModified === true)
            .map((e: AppNotificationUserEvent) => e.getValue());
        data = this.updateSubmitData(data);
        this.appNotificationService.saveUserData(data).subscribe(
            (success: boolean) => {
                const msg = success ? 'Saved Successfully' : 'Unable To Save';
                this.showAlert(msg);
            }
        );
    }
    /**
     *
     * @param data
     */
    private updateSubmitData(data: IAppNotificationUserEvent[]) {
        Object.keys(this.SELECTION_LEVEL).forEach((selectionLevel: string) => {
            if (!this.SELECTION_LEVEL[selectionLevel].isActive) {
                return;
            }
            data = data.concat(this.SELECTION_LEVEL[selectionLevel].data);
        });
        return data;
    }
    notifyThisUser(): void {
        const data = {
            'eventCode': 'QUOTE_ACCEPTED',
            'message': 'mail triggered',
            'receiver': [this._userProfile.email],
            'options': {
                'email': {
                    'cc': [],
                    'isHTML': false,
                    'subject': 'testing app notification',
                    'messageAttributes': {
                        'user': ' ',
                        'today': '15th November 2018'
                    }
                },
                'inapp': {
                    'messageAttributes': {}
                }
            }
        };
        this.appNotificationService.notifyThisUser(data);
    }
    /**
     * undo all unsaved changes
     */
    undoChanges(): void {
        this._userData = this._userDataOriginal.map((e: AppNotificationUserEvent) => {
            const val = { ...e.getValue() };
            return new AppNotificationUserEvent(val);
        });
        Object.keys(this.SELECTION_LEVEL).forEach((level: string) => {
            this.SELECTION_LEVEL[level].data.splice(0);
        });
        this.updateNotificationGroups();
        this.updateNotificationEvents();
        this.showAlert('Unsaved Changes Discarded');
    }
    ngOnDestroy() {
        this.allNotificationEvents$.unsubscribe();
        this.userData$.unsubscribe();
        this.notificationPreferencesUpdatedSubscription$.unsubscribe();
        this.notificationGroups$.unsubscribe();
    }
}


